import { useEffect } from 'react';
import { generateAlertId, resetAlertCounter } from '../../../utils/alertIdGenerator';
import { formatDate, formatAmount, getCountryName, extractRuleName } from '../../../utils/formatters';

interface TableRowData {
  alertId: string;
  date: string;
  transactionReference: string;
  ruleTriggered: string;
  sender: string;
  receiver: string;
  countryFrom: string;
  countryTo: string;
  amount: string;
  channel: string;
}

interface ValidationResultsTableProps {
  results: any[];
  payments?: any[];
}

function ValidationResultsTable({ results, payments = [] }: ValidationResultsTableProps): JSX.Element {
  // Reset alert counter when component mounts or results change
  useEffect(() => {
    resetAlertCounter();
  }, [results]);
  
  // Debug logging
  console.log('ValidationResultsTable - payments prop:', payments);
  console.log('ValidationResultsTable - payments length:', payments.length);
  console.log('ValidationResultsTable - results length:', results.length);
  if (payments.length > 0) {
    console.log('ValidationResultsTable - first payment:', payments[0]);
  }
  
  // Create a map of paymentId/endToEndId to payment data for quick lookup
  const paymentMap = new Map<string, any>();
  payments.forEach((payment: any) => {
    if (payment.paymentId) {
      paymentMap.set(payment.paymentId, payment);
    }
    if (payment.endToEndId) {
      paymentMap.set(payment.endToEndId, payment);
    }
  });
  
  console.log('ValidationResultsTable - paymentMap size:', paymentMap.size);
  
  // Transform results into table rows, filtering out those without triggered rules
  const tableRows: TableRowData[] = results
    .map((result: any, index: number) => {
      // Extract rule name first to check if rule is triggered
      const ruleTriggered = extractRuleName(result.validationMessages, result.issues);
      
      // Skip if no rule is triggered
      if (ruleTriggered === 'N/A') {
        return null;
      }
      
      // Find the payment - try multiple matching strategies
      let payment: any = null;
      
      // Strategy 1: Use payment data directly embedded in result (most reliable)
      if (result.payment) {
        payment = result.payment;
      }
      // Strategy 2: Match by result.index (most reliable since results and payments are in same order)
      else {
        const resultIndex = result.index !== undefined ? result.index : index;
        if (payments.length > resultIndex) {
          payment = payments[resultIndex];
        }
        // Strategy 3: Match by endToEndId
        if (!payment && result.endToEndId) {
          payment = paymentMap.get(result.endToEndId);
          if (!payment) {
            payment = payments.find((p: any) => p.endToEndId === result.endToEndId || p.paymentId === result.endToEndId);
          }
        }
        // Strategy 4: Match by paymentId if available in result
        if (!payment && result.paymentId) {
          payment = paymentMap.get(result.paymentId);
        }
        // Strategy 5: Fallback to array index
        if (!payment && payments.length > index) {
          payment = payments[index];
        }
      }
      
      // If still no payment found, log for debugging
      if (!payment) {
        console.warn('Payment not found for result:', {
          hasEmbeddedPayment: !!result.payment,
          resultIndex: result.index,
          arrayIndex: index,
          endToEndId: result.endToEndId,
          paymentId: result.paymentId,
          result: result,
          paymentsLength: payments.length
        });
        payment = {};
      }
      
      // Generate unique Alert ID only for rows with triggered rules
      const alertId = generateAlertId();
      
      // Extract data - prefer embedded payment data, fallback to matched payment
      const transactionReference = result.endToEndId || payment.endToEndId || payment.paymentId || result.paymentId || `payment_${index}`;
      const sender = result.payment?.debtorName || payment.debtorName || 'N/A';
      const receiver = result.payment?.creditorName || payment.creditorName || 'N/A';
      const countryFrom = getCountryName(result.payment?.debtorIban || payment.debtorIban);
      const countryTo = getCountryName(result.payment?.creditorIban || payment.creditorIban);
      
      // Ensure amount is properly extracted - handle both number and string formats
      // Try to get amount from embedded payment first, then from matched payment
      console.log('Debug result.payment:', result.payment);
      console.log('Debug payment:', payment);
      let paymentAmount = result.payment?.amount ?? payment.amount;
      
      // Debug logging for specific transaction
      if (transactionReference === 'INV-20251006-004') {
        console.log('Debug INV-20251006-004:', {
          resultPayment: result.payment,
          matchedPayment: payment,
          paymentAmount,
          transactionReference
        });
      }
      
      if (paymentAmount === undefined || paymentAmount === null) {
        paymentAmount = 0;
      }
      if (typeof paymentAmount === 'string') {
        paymentAmount = parseFloat(paymentAmount) || 0;
      }
      const amount = formatAmount(paymentAmount, result.payment?.currency || payment.currency || 'EUR');
      const channel = payment.type === 'SCT' ? 'SEPA Credit Transfer' : (payment.type || 'SEPA Credit Transfer');
      
      // Use payment date if available, otherwise use current date
      const date = payment.date ? formatDate(payment.date) : formatDate(new Date());
      
      return {
        alertId,
        date,
        transactionReference,
        ruleTriggered,
        sender,
        receiver,
        countryFrom,
        countryTo,
        amount,
        channel
      };
    })
    .filter((row): row is TableRowData => row !== null); // Filter out null entries
  
  if (tableRows.length === 0) {
    return <div className="App-table-empty">No validation results to display</div>;
  }
  
  return (
    <div className="App-table-container">
      <table className="App-validation-table">
        <thead>
          <tr>
            <th>Alert ID</th>
            <th>Date</th>
            <th>Transaction Reference</th>
            <th>Rule Triggered</th>
            <th>Sender</th>
            <th>Receiver</th>
            <th>Country From</th>
            <th>Country to</th>
            <th>Amount</th>
            <th>Channel</th>
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row, index) => (
            <tr key={index}>
              <td>{row.alertId}</td>
              <td>{row.date}</td>
              <td>{row.transactionReference}</td>
              <td>{row.ruleTriggered}</td>
              <td>{row.sender}</td>
              <td>{row.receiver}</td>
              <td>{row.countryFrom}</td>
              <td>{row.countryTo}</td>
              <td>{row.amount}</td>
              <td>{row.channel}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ValidationResultsTable;

